import { useState, useRef } from "react"
import styled from "styled-components"
import { Formik, Form, Field } from "formik"
import ReCAPTCHA from "react-google-recaptcha"
import { graphql, useStaticQuery } from "gatsby"
import { Box, FormField, FormFieldSubmit, Text } from "@fastly/consistently"
import { MikanSpan } from "../Mikan"
import { validateForm, REGEXES } from "./form-utils/validation"
import { reduceMicrocopySet } from "@utils/reduceMicrocopy"
import useSubmit from "@utils/useSubmit"

const StyledForm = styled(Box).attrs({ as: Form })`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 16px;
  width: 100%;
  max-width: 18rem;
  margin-left: auto;
  margin-right: auto;

  label {
    text-align: left;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    border: 0 solid var(--white);
    -webkit-text-fill-color: var(--grey-90);
    box-shadow: 0 0 0px 1000px var(--white) inset;
    transition: background-color 5000s ease-in-out 0s;
  }
`

const NewsletterForm = ({ formId = "1849", ...props }) => {
  const { handleSubmit, submitted, submissionFailed } = useSubmit()
  const [isFocused, setFocused] = useState(false)
  const recaptchaRef = useRef()

  const newsletterLabels = useStaticQuery(
    graphql`
      {
        emailLabel: contentfulMicrocopy(contentful_id: { eq: "P3SbmtOLwZnsItrXb2Ctv" }) {
          id
          text
          title
        }
        successLabel: contentfulMicrocopy(contentful_id: { eq: "kgvj12vB43lDXk262qh56" }) {
          id
          text
          title
        }
        failLabel: contentfulMicrocopy(contentful_id: { eq: "cLiHZF91r1RNQUfSvsnK2" }) {
          id
          text
          title
        }
        newsletterLabel: contentfulMicrocopy(contentful_id: { eq: "2wgwTvdh1fcRQMw7QrDmwN" }) {
          id
          text
          title
        }
        newsletterPlaceholder: contentfulMicrocopy(contentful_id: { eq: "3Tp9HtCvZ61uvQYSA18aAu" }) {
          id
          text
          title
        }
      }
    `
  )

  const labels = reduceMicrocopySet({ items: Object.values(newsletterLabels) })

  return (
    <Formik
      initialValues={{
        Email: "",
        Opt_in: true,
        Opt_In_GDPR__c: true,
        formId: formId,
        formVid: formId,
      }}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values, setSubmitting, recaptchaRef)
      }}
      validate={(values) => {
        const valuesToTest = ["Email"]
        const defaultMessage = labels[`field.error.required`]
        const errors = validateForm(valuesToTest, values, defaultMessage, {
          Email: { message: labels[`field.error.email`], regex: REGEXES.email },
        })

        return errors
      }}
    >
      {({ isSubmitting }) => (
        <StyledForm id="newsletter-form" {...props} onFocus={() => setFocused(true)}>
          {submitted ? (
            <Text as="p">
              <MikanSpan text={labels["thanks-for-subscribing"]} />
            </Text>
          ) : (
            <>
              <Field name="Email">
                {({ field, meta }) => (
                  <FormField
                    required
                    type="email"
                    label={<MikanSpan text={labels["field.email"]} />}
                    placeholder={labels["email-placeholder-text"]}
                    disabled={submitted}
                    {...meta}
                    {...field}
                  />
                )}
              </Field>
              <FormFieldSubmit variant="main" loading={isSubmitting}>
                <MikanSpan text={labels["sign-up-for-our-newsletter"]} />
              </FormFieldSubmit>
              {submissionFailed && (
                <Text as="p" variant="sm" color="var(--red-dark)">
                  <MikanSpan text={labels["oh-no-something-went-wrong-please-try-again-later"]} />
                </Text>
              )}
              {isFocused && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.GATSBY_RECAPTCHA_SITEKEY || "6LcQLjEUAAAAAMGtzNz-AGhMH6vrXBbBTw0h9TLO"}
                  badge="inline"
                />
              )}
            </>
          )}
        </StyledForm>
      )}
    </Formik>
  )
}

export default NewsletterForm
